import {Component} from '@packages/systems/wfdl';
import {BASE_VARIANT_DEFAULTS} from '../constants';
import {escapeRegExp} from 'lodash';

/**
 * Gets the display name for the base variant, whether variants exist or not.
 *
 * - If variants exist: returns the base variant's `displayName` from variant options.
 * - If no variants: returns the value in `component.placeholderBaseVariantName`.
 *
 * If neither of the above are set, returns the global default "Base" name.
 */
export function getBaseVariantDisplayName(
  component: Component.ComponentType
): string {
  const variants = Component.getVariants(component);

  if (variants && Object.keys(variants).length > 0) {
    const variantFieldId = Object.keys(variants)[0];
    const baseVariant = variantFieldId
      ? variants[variantFieldId]?.find((v) => v.id === BASE_VARIANT_DEFAULTS.id)
      : null;

    return baseVariant?.displayName ?? BASE_VARIANT_DEFAULTS.displayName;
  }

  return (
    Component.getPlaceholderBaseVariantName(component) ??
    BASE_VARIANT_DEFAULTS.displayName
  );
}

/**
 * Sets a placeholder name for the base variant under `component.placeholderBaseVariantName`
 * when no other component variants exist.
 *
 * If the provided name is equal to the global "Base" default,
 * returns the component with `placeholderBaseVariantName` reset instead.
 */
export function setPlaceholderBaseVariantName(
  component: Component.ComponentType,
  name: string
): Component.ComponentType {
  return Component.update(component, {
    placeholderBaseVariantName:
      name !== BASE_VARIANT_DEFAULTS.displayName ? name : undefined,
  });
}

/**
 * Copies the provided component's `placeholderBaseVariantName` to the base variant's `displayName` and
 * returns the component with updated variants field.
 *
 * Sets `component.variants` under the provided `variantFieldId` where the base variant option
 * uses a `displayName` from `component.placeholderBaseVariantName`, or the global "Base"
 * default if a custom placeholder is not set.
 *
 * Note: this function clears the current `component.placeholderBaseVariantName` as this value
 * is intended to act as a placeholder until a base variant exists in a `component.variants` collection.
 */
export function setVariantsWithDefaultBase(
  component: Component.ComponentType,
  variantFieldId: string,
  variants: Component.StyleVariant[]
): Component.ComponentType {
  // Additional check to exclude an existing base variant, if present,
  // as this function returns a default base variant option
  const nonBaseVariants = variants.filter(
    (v) => v.id !== BASE_VARIANT_DEFAULTS.id
  );
  const displayName =
    Component.getPlaceholderBaseVariantName(component) ??
    BASE_VARIANT_DEFAULTS.displayName;

  const baseVariant: Component.StyleVariant = {
    id: BASE_VARIANT_DEFAULTS.id,
    displayName,
  };

  return Component.update(component, {
    variants: {
      ...component.variants,
      [variantFieldId]: [baseVariant, ...nonBaseVariants],
    },
    placeholderBaseVariantName: undefined,
  });
}

export function getNextVariantNameForDuplication(
  baseName: string,
  existingNames: string[]
): string {
  // remove any existing number from the base name
  const baseNameWithoutNumber = baseName.replace(/\s*\d+$/, '');

  // get all existing numbers from variant names
  const existingNumbers = existingNames
    .map((displayName) => {
      const match = displayName.match(
        `^${escapeRegExp(baseNameWithoutNumber)}\\s*(\\d+)$`
      );
      return match && match[1] ? parseInt(match[1], 10) : 0;
    })
    .filter((num): num is number => num > 0);

  // determine the next number in the sequence
  const nextNumber =
    existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 2;

  return `${baseNameWithoutNumber} ${nextNumber}`;
}

/**
 * Copies the name of a base variant under `variantFieldId` to the component's `placeholderBaseVariantName`.
 *
 * This function is used to keep the current custom base variant name in flows where
 * the base variant is removed, such as when a style variant property is deleted.
 */
export function copyBaseVariantNameToPlaceholder(
  component: Component.ComponentType,
  variantFieldId: string
): Component.ComponentType {
  const variants = Component.getVariants(component);
  const baseVariant = variants?.[variantFieldId]?.find(
    (v) => v.id === BASE_VARIANT_DEFAULTS.id
  );

  const baseVariantDisplayName = baseVariant?.displayName;

  return Component.update(component, {
    placeholderBaseVariantName:
      baseVariantDisplayName &&
      // Only store names that are not using the global "Base" default value
      baseVariantDisplayName !== BASE_VARIANT_DEFAULTS.displayName
        ? baseVariantDisplayName
        : undefined,
  });
}
